import { useQuery } from '@tanstack/react-query';
import {
  FeeStatus,
  PaymentPlanStatus,
  type StudentFeeFilter,
  type UseQueryReturnType,
  gqlClient,
  graphql,
  queryClient,
} from '@tyro/api';
import dayjs from 'dayjs';
import { feeKeys } from './keys';
import { getPaymentPlanInstallments } from './payment-plan-installments';

const studentFees = graphql(/* GraphQL */ `
  query fees_studentFees($filter: StudentFeeFilter) {
    fees_studentFees(filter: $filter) {
      id {
        feeId
        studentPartyId
      }
      person {
        partyId
        firstName
        lastName
        avatarUrl
        type
        title {
          id
          name
          nameTextId
        }
      }
      feeName
      dueDate
      amount
      amountPaid
      amountDue
      feeType
      feeStatus
      amountDiscounted
      adHocAmount
      discounts {
        id
        name
        description
        discountType
        value
        siblingDiscount
      }
      amountWrittenOff
      writeOffNote
      allowPartialPayments
      classGroup {
        name
      }
      paymentPlan
      paymentPlanInterval
      paymentPlanIntervalCount
      paymentPlans {
        id
        absorbFees
        status
        cardUsed
        payee {
          partyId
          firstName
          lastName
          avatarUrl
          type
          title {
            id
            name
            nameTextId
          }
        }
        installments {
          installment
          installmentDate
          status
          amount
          serviceCharges
        }
      }
    }
  }
`);

const studentFeesQuery = (filter: StudentFeeFilter) => ({
  queryKey: feeKeys.studentFees(filter),
  queryFn: async () => {
    const data = await gqlClient.request(studentFees, { filter });

    const activePaymentPlansMap = new Map(
      data.fees_studentFees.map(({ id, paymentPlans }) => [
        `${id.studentPartyId}${id.feeId}`,
        paymentPlans?.find(
          ({ status, installments }) =>
            status === PaymentPlanStatus.Active && installments.length > 0,
        ),
      ]),
    );

    const installmentsResults = await getPaymentPlanInstallments({
      ids: data.fees_studentFees
        .filter(
          ({ id, paymentPlan }) =>
            paymentPlan &&
            !activePaymentPlansMap.get(`${id.studentPartyId}${id.feeId}`),
        )
        .map((fee) => fee.id),
    });

    const installmentsMap = new Map(
      installmentsResults.map(({ id, installments }) => [
        `${id?.studentPartyId}${id?.feeId}`,
        installments,
      ]),
    );

    return {
      ...data,
      fees_studentFees: data.fees_studentFees.map((studentFee) => {
        const { feeId, studentPartyId } = studentFee.id;
        const planId = `${studentPartyId}${feeId}`;

        const activePaymentPlan = activePaymentPlansMap.get(planId);
        const paymentPlanInstallments = installmentsMap.get(planId);

        return {
          ...studentFee,
          feeStatus:
            studentFee.amountDue <= 0
              ? FeeStatus.Complete
              : studentFee.feeStatus,
          paymentPlanInstallments: paymentPlanInstallments || [],
          activePaymentPlan: activePaymentPlan
            ? {
                ...activePaymentPlan,
                installments: [...(activePaymentPlan.installments || [])].sort(
                  (a, b) =>
                    dayjs(a.installmentDate).unix() -
                    dayjs(b.installmentDate).unix(),
                ),
              }
            : null,
        };
      }),
    };
  },
});

export function getStudentFees(filter: StudentFeeFilter) {
  return queryClient.fetchQuery(studentFeesQuery(filter));
}

export function useStudentFees(filter: StudentFeeFilter) {
  return useQuery({
    ...studentFeesQuery(filter),
    select: ({ fees_studentFees }) => fees_studentFees,
  });
}

export type ReturnTypeFromUseStudentFees = UseQueryReturnType<
  typeof useStudentFees
>[number];
